import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    CodeBlock,
    BlockType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    SettingOption,
} from '@appblocks/codegeneration/types';
import LUISItemOption from '@appblocks/codegeneration/SettingsConfigurations/LUISItemOption';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

export default class LUISItem extends BaseFeature {
    constructor() {
        super();
        this.name = 'Setting Item';
        this.id = 'luis_item';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'SETTINGID',
                name: 'Connected Setting',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                refId: 'stg_setting',
                options: [],
            },
            {
                id: 'NAME',
                name: 'Setting Display Name',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'CONTROL',
                name: 'UI Control Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'EDIT',
                options: [
                    {
                        name: 'Text Box',
                        value: 'EDIT',
                    },
                    {
                        name: 'Password',
                        value: 'PASSWORD',
                    },
                    {
                        name: 'Dropdown',
                        value: 'STATIC',
                    },
                    {
                        name: 'IP Address',
                        value: 'IPCTRL',
                    },
                    // {
                    //     name: '6-byte ethernet MAC-address',
                    //     value: 'ETHERNETCTRL',
                    // },
                ],
            },
            {
                id: 'OPTIONS',
                name: 'Options',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'luis_item_option',
                default: [
                    new LUISItemOption(),
                ],
            },
            {
                id: 'VALIDATION',
                name: 'Validation of Value',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '',
            },
            {
                id: 'STATUS',
                name: 'Status of Control',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '',
            },
            {
                id: 'READONLY',
                name: 'Edit Mode',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Read/Write',
                        value: '0',
                    },
                    {
                        name: 'Read Only',
                        value: '1',
                    },
                ],
            },
        ]);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'SETTINGID') {
            const settings = this.project.getFeature('stg').getSettingValue('SETTINGS');
            const result = [];
            result.push({
                name: 'Undefined',
                value: '',
            });
            for (let i = 0; i < settings.length; i++) {
                const setting = settings[i];
                const settingId = setting.getSettingValue('ID');
                result.push({
                    value: settingId,
                    name: `${setting.getSettingValue('NAME')} (${setting.getSettingValue('SETTINGID')})`,
                });
            }
            return result;
        }
        if (settingId === 'CONTROL') {
            const group = this.project.getParent(this);
            const feature = this.project.getParent(group);
            if (feature.id === 'ds_man') {
                return [
                    {
                        name: 'Text Box',
                        value: 'EDIT',
                    },
                    {
                        name: 'Dropdown',
                        value: 'STATIC',
                    },
                    {
                        name: 'IP Address',
                        value: 'IPCTRL',
                    },
                ];
            }
        }
        return super.getSettingOptions(settingId);
    }

    getDependencies() {
        const dependencies = [
            'luis',
        ];
        return dependencies;
    }

    getSettingValue(settingId: string, getStoredValue?: boolean) {
        return super.getSettingValue(settingId, getStoredValue);
    }

    setSettingValue(settingId: string, value: string) {
        const project = this.project;
        if (settingId === 'SETTINGID' && value === '') {
            this.setSettingValue('NAME', '');
        }
        if (settingId === 'SETTINGID' && project) {
            const settings = project.getFeature('stg').getSettingValue('SETTINGS');
            for (let i = 0; i < settings.length; i++) {
                const setting = settings[i];
                const id = setting.getSettingValue('ID');
                if (id === value) {
                    this.setSettingValue('NAME', '');
                    break;
                }
            }
        }
        super.setSettingValue(settingId, value);
    }

    getLinkedSetting() {
        const { features } = this.project;
        const settingID = this.getSettingValue('SETTINGID');
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            if (feature.id === 'linked_setting') {
                const linkedSettingID = feature.getSettingValue('LINKEDSETTING');
                if (linkedSettingID === settingID) {
                    const refSettingId = feature.getSettingValue('SETTINGID');
                    const refFeatureId = feature.getSettingValue('FEATURE');
                    const refFeature = this.project.getFeature(refFeatureId);
                    if (refFeature && refFeature.getSetting(refSettingId) !== undefined) {
                        const UIControl = refFeature.getSetting(refSettingId).UIControl;
                        return UIControl;
                    }
                }
            }
        }
        return undefined;
    }

    getState(settingId: string): SettingState {
        const control = this.getSettingValue('CONTROL');
        const linkedSetting = this.getLinkedSetting();
        switch (settingId) {
            case 'CONTROL':
                if (linkedSetting) return SettingState.DISABLED;
                return SettingState.ENABLED;
            case 'OPTIONS':
                if (control === 'STATIC') {
                    if (linkedSetting) return SettingState.DISABLED;
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            case 'NAME':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];

        const { project } = this;
        const settings = project.getSettingValue('stg', 'SETTINGS');
        const settingID = this.getSettingValue('SETTINGID');
        const parent = project.getParent(project.getParent(this));
        let found = false;
        const UIControl = this.getLinkedSetting();
        if (UIControl) {
            const UIControlSettings = Object.entries(UIControl);
            for (let j = 0; j < UIControlSettings.length; j++) {
                let settingValue: any = UIControlSettings[j][1];
                if (UIControlSettings[j][0] === 'OPTIONS') {
                    const options = UIControlSettings[j][1] as { name: string, value: string }[];
                    settingValue = [];
                    for (let k = 0; k < options.length; k++) {
                        const newLuisItemOption = new LUISItemOption();
                        newLuisItemOption.setSettingValue('LABEL', options[k].name);
                        newLuisItemOption.setSettingValue('VALUE', options[k].value);
                        settingValue.push(newLuisItemOption);
                    }
                    if (this.getSettingValue('OPTIONS').length !== settingValue.length) {
                        const arr = [];
                        for (let k = 0; k < settingValue.length; k++) {
                            arr.push(FeatureUtils.toJson(settingValue[k]));
                        }
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            message: '',
                            featureId: this.getID(),
                            settingId: 'OPTIONS',
                            data: arr,
                        });
                    }
                }
                if (UIControlSettings[j][0] === 'CONTROL') {
                    if (this.getSettingValue(UIControlSettings[j][0]) !== settingValue) {
                        let found = false;
                        const options = this.getSettingOptions('CONTROL');
                        if (options) {
                            for (let k = 0; k < options.length; k++) {
                                if (options[k].value === settingValue) {
                                    found = true;
                                    break;
                                }
                            }
                        }
                        if (found) {
                            errors.push({
                                messageType: ValidationMessageType.UPDATE,
                                message: '',
                                featureId: this.getID(),
                                settingId: 'CONTROL',
                                data: settingValue,
                            });
                        }
                    }
                }
            }
        }
        for (let i = 0; i < settings.length; i += 1) {
            if (settings[i].getSettingValue('ID') === settingID) {
                if (this.getSettingValue('NAME') === '') {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'NAME',
                        data: settings[i].getSettingValue('NAME'),
                    });
                }
                found = true;
                break;
            }
        }
        if (!found && parent.getEnabled()) {
            errors.push({
                message: 'Setting is not defined',
                messageType: ValidationMessageType.ERROR,
                featureId: parent.getID(),
                settingId: 'SETTINGID',
            });
        }

        return errors;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        const ID = this.getSettingValue('SETTINGID');
        let SETTING;
        const CONTROL = this.getSettingValue('CONTROL');
        const NAME = this.getSettingValue('NAME');
        let settingItem;
        let settingType = '';
        let MIN = '';
        let MAX = '';

        const { project } = this;
        const settings = project.getSettingValue('stg', 'SETTINGS');
        for (let i = 0; i < settings.length; i += 1) {
            if (settings[i].getSettingValue('ID') === ID) {
                settingItem = settings[i];
                SETTING = settings[i].getSettingValue('SETTINGID');
                switch (settings[i].getSettingValue('DATATYPE')) {
                    case 'F':
                    case 'B':
                    case 'W':
                        settingType = 'INT';
                        break;
                    case 'S':
                        settingType = 'STRING';
                        break;
                    case 'T':
                        settingType = 'DATETIME';
                        break;
                    case 'M':
                        settingType = 'TIME';
                        break;
                    default:
                        settingType = 'STRING';
                        break;
                }
            }
        }

        if (SETTING === undefined) {
            return blocks;
        }

        let fID = SETTING;
        if (fID.length > 2) {
            fID = `${SETTING}@`;
        }

        let validation = '';
        if (settingItem) {
            MIN = settingItem.getSettingValue('MIN');
            MAX = settingItem.getSettingValue('MAX');
            const DATATYPE = settingItem.getSettingValue('DATATYPE');
            if (DATATYPE === 'F' || DATATYPE === 'B' || DATATYPE === 'W') { // floating point number
                if (MIN === '') {
                    MIN = '-2147483647';
                }
                if (MAX === '') {
                    MAX = '2147483647';
                }
                validation = `V=${fID}<=${MAX}&&${fID}>=${MIN}?"":"Value must be between ${MIN} and ${MAX}";`;
            }
        }
        if (settingType === 'S') { // string
            if (MAX === '') {
                MAX = '240';
            }
            if (MIN === '') {
                MIN = '0';
            }
            validation = `V=${fID}.length<=${MAX}&&${fID}.length>=${MIN}?"":"Value must be between ${MIN} and ${MAX} characters";`;
        }
        if (this.getSettingValue('VALIDATION') !== '') {
            validation = `V=${this.getSettingValue('VALIDATION')};`;
        }
        const status = '';
        if (this.getSettingValue('STATUS') !== '') {
            validation = `S=${this.getSettingValue('STATUS')};`;
        }
        let options = '';
        let menuOptions = '';
        if (CONTROL === 'STATIC') {
            const tmpOptions = this.getSettingValue('OPTIONS');
            const tmpCodes = [];
            for (let i = 0; i < tmpOptions.length; i += 1) {
                const tmpBlocks = tmpOptions[i].getCodeBlocks();
                for (let j = 0; j < tmpBlocks.length; j += 1) {
                    if (tmpBlocks[j].location === 'sdf.xtxt') {
                        tmpCodes.push(tmpBlocks[j].template);
                    }
                    if (tmpBlocks[j].location === 'menu.xtxt') {
                        menuOptions += `${tmpBlocks[j].template};`;
                    }
                }
            }

            options = `O=${tmpCodes.join('/')};`;
        }

        blocks.push({
            location: 'sdf.xtxt',
            blockType: BlockType.CODE,
            template: `
I=${fID};T=${settingType};C=${CONTROL};D=${NAME};${validation}${status}${options}`,
        });

        const itemType = 'S'; // S=setting; C=Command;
        let inputType = 'I';// I=input; L=dropdown list
        let inputCharacters = 'B';// B=both; D=Digits; A=Alphabet
        let mask = '';
        let maxLength = 0;
        const hideCharacters = 'N'; // N=No

        for (let i = 0; i < settings.length; i += 1) {
            if (settings[i].getSettingValue('SETTINGID') === SETTING) {
                maxLength = settings[i].getSettingValue('MAX');
                break;
            }
        }

        const controlType = this.getSettingValue('CONTROL');
        switch (controlType) {
            case 'EDIT':
                maxLength = Number(MAX);
                if (settingType === 'INT') {
                    inputCharacters = 'D';
                    maxLength = 32;
                } else {
                    maxLength = 240;
                    inputCharacters = 'B';
                }
                switch (settingType) {
                    case 'INT':
                        inputCharacters = 'D';
                        maxLength = 32;
                        break;
                    case 'DATETIME':
                        inputCharacters = 'C';
                        maxLength = 13;
                        mask = '    /  /  -  :  ';
                        break;
                    case 'TIME':
                        inputCharacters = 'C';
                        maxLength = 4;
                        mask = '  :  ';
                        break;
                    default:
                        maxLength = 240;
                        inputCharacters = 'B';
                }
                break;
            case 'STATIC':// dropdown
                inputType = 'L';
                break;
            case 'IPCTRL':
                inputCharacters = 'C';
                mask = '   .   .   .   ';
                maxLength = 12;
                break;
            case 'ETHERNETCTRL':
                inputCharacters = 'B';
                mask = '  :  :  :  :  :  ';
                maxLength = 12;
                break;
            default:
                inputType = 'I';
                break;
        }
        const itemOptions = `${mask},${inputCharacters},${maxLength},${hideCharacters}`;

        blocks.push({
            location: 'menu.xtxt',
            blockType: BlockType.CODE,
            template: `
>>0\t1\tN\t${NAME}\t${itemType},${inputType},${fID},${menuOptions === '' ? itemOptions : menuOptions}`,
        });

        blocks.push({
            location: 'luis.xtxt',
            blockType: BlockType.CODE,
            template: `
I=${SETTING};T=${settingType};C=${CONTROL};D=${NAME};${validation}${status}${options}`,
        });

        return blocks;
    }
}
